import { getAcademicYearSettings, loadAcademicYearSettingsCache } from '../services/calendarService';
import {
  getAcademicCalendarSections,
  loadAcademicCalendarSectionsCache,
  type PageSectionLayout,
} from '../services/sectionLayoutService';
import type { AcademicTerm, CalendarDay } from '../types/domain';
import { addDays, parseISODate, toISODate } from './date';

export const ACADEMIC_SECTION_CALENDAR_CHANGE_EVENT = 'cp:academic-section-calendar-change';

/** `AcademicCalendarPage`와 동일 — 섹션별 학년도·기간 설정 */
export interface AcademicSectionCalendarSettings {
  academicYear: number;
  yearStartsOn: string;
  yearEndsOn: string;
  summerBreakStartsOn: string;
  summerBreakEndsOn: string;
  winterBreakStartsOn: string;
  winterBreakEndsOn: string;
}

export interface SectionTermRanges {
  term1StartsOn: string;
  term1EndsOn: string;
  term2StartsOn: string;
  term2EndsOn: string;
  calendarStartsOn: string;
  calendarEndsOn: string;
}

/** 연간시간표·학사일정 구독에 쓰는 클래스별 학사일정 컨텍스트 */
export interface CalendarLinkedContext {
  sectionId: string;
  academicYear: number;
  terms: AcademicTerm[];
  calendarStartsOn: string;
  calendarEndsOn: string;
  /** true: `calendar_days` DB · false: 섹션 전용 LS 공휴일 */
  useDbCalendarDays: boolean;
  localCalendarDays: CalendarDay[];
  sectionSettings: AcademicSectionCalendarSettings | null;
}

const SUMMER_BREAK_VACATION_NOTE = '여름방학';
const WINTER_BREAK_VACATION_NOTE = '겨울방학';

function inClosedRange(day: string, from: string, to: string): boolean {
  return Boolean(from && to && day >= from && day <= to);
}

export function isDateInSummerBreak(settings: AcademicSectionCalendarSettings, dateIso: string): boolean {
  const from = (settings.summerBreakStartsOn ?? '').trim();
  const to = (settings.summerBreakEndsOn ?? '').trim();
  return inClosedRange(dateIso, from, to);
}

export function isDateInWinterBreak(settings: AcademicSectionCalendarSettings, dateIso: string): boolean {
  const from = (settings.winterBreakStartsOn ?? '').trim();
  const to = (settings.winterBreakEndsOn ?? '').trim();
  return inClosedRange(dateIso, from, to);
}

/** 방학 기간(여름·겨울) 여부 — 연간시간표 자동 비수업일 판정용 */
export function isDateInSchoolBreak(settings: AcademicSectionCalendarSettings | null | undefined, dateIso: string): boolean {
  if (!settings || !dateIso) return false;
  return isDateInSummerBreak(settings, dateIso) || isDateInWinterBreak(settings, dateIso);
}

/** 이전에 자동 등록된 방학 vacation 행 제거(공휴일 목록·DB에는 넣지 않음) */
export function stripAutoBreakVacationCalendarDays(days: CalendarDay[]): CalendarDay[] {
  return days.filter((d) => {
    if (d.day_kind !== 'vacation') return true;
    const note = (d.note ?? '').trim();
    return note !== SUMMER_BREAK_VACATION_NOTE && note !== WINTER_BREAK_VACATION_NOTE;
  });
}

/** @deprecated 방학은 공휴일이 아니라 연간시간표 자동 비수업(`isDateInSchoolBreak`)으로 처리 */
export function mergeBreakVacationCalendarDays(
  settings: AcademicSectionCalendarSettings,
  days: CalendarDay[],
  _workspaceId: string
): CalendarDay[] {
  return stripAutoBreakVacationCalendarDays(days);
}

export function defaultAcademicSectionSettings(y: number): AcademicSectionCalendarSettings {
  return {
    academicYear: y,
    yearStartsOn: `${y}-03-01`,
    yearEndsOn: `${y + 1}-02-28`,
    summerBreakStartsOn: `${y}-07-01`,
    summerBreakEndsOn: `${y}-08-31`,
    winterBreakStartsOn: '',
    winterBreakEndsOn: '',
  };
}

export function academicSectionSettingsStorageKey(workspaceId: string, sectionId: string): string {
  return `cp.academicSectionSettings.${workspaceId}.${sectionId}`;
}

export function academicSectionCalendarDaysStorageKey(workspaceId: string, sectionId: string): string {
  return `cp.academicSectionCalendarDays.${workspaceId}.${sectionId}`;
}

export function emitAcademicSectionCalendarChange(workspaceId: string, sectionId: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(ACADEMIC_SECTION_CALENDAR_CHANGE_EVENT, {
      detail: { workspaceId, sectionId },
    })
  );
}

export function readAcademicSectionSettingsFromLocalStorage(
  workspaceId: string,
  sectionId: string
): AcademicSectionCalendarSettings | null {
  if (!workspaceId || !sectionId || sectionId === 'base' || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(academicSectionSettingsStorageKey(workspaceId, sectionId));
    if (raw == null || raw === '') return null;
    const parsed = JSON.parse(raw) as Partial<AcademicSectionCalendarSettings>;
    const y = Number(parsed.academicYear);
    if (!Number.isFinite(y) || y < 2000 || y > 2100) return null;
    return {
      academicYear: Math.floor(y),
      yearStartsOn: String(parsed.yearStartsOn ?? ''),
      yearEndsOn: String(parsed.yearEndsOn ?? ''),
      summerBreakStartsOn: String(parsed.summerBreakStartsOn ?? ''),
      summerBreakEndsOn: String(parsed.summerBreakEndsOn ?? ''),
      winterBreakStartsOn: String(parsed.winterBreakStartsOn ?? ''),
      winterBreakEndsOn: String(parsed.winterBreakEndsOn ?? ''),
    };
  } catch {
    return null;
  }
}

export function writeAcademicSectionSettingsToLocalStorage(
  workspaceId: string,
  sectionId: string,
  settings: AcademicSectionCalendarSettings
): void {
  if (!workspaceId || !sectionId || sectionId === 'base' || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      academicSectionSettingsStorageKey(workspaceId, sectionId),
      JSON.stringify(settings)
    );
    emitAcademicSectionCalendarChange(workspaceId, sectionId);
  } catch {
    /* ignore */
  }
}

/** 삭제된 섹션의 LS 잔여 데이터 제거(삭제 후 서버 백필/새로고침으로 부활 방지) */
export function clearAcademicSectionLocalStorage(workspaceId: string, sectionId: string): void {
  if (!workspaceId || !sectionId || sectionId === 'base' || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(academicSectionSettingsStorageKey(workspaceId, sectionId));
    window.localStorage.removeItem(academicSectionCalendarDaysStorageKey(workspaceId, sectionId));
    emitAcademicSectionCalendarChange(workspaceId, sectionId);
  } catch {
    /* ignore */
  }
}

export function readAcademicSectionCalendarDaysFromLocalStorage(
  workspaceId: string,
  sectionId: string
): CalendarDay[] {
  if (!workspaceId || !sectionId || sectionId === 'base' || typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(academicSectionCalendarDaysStorageKey(workspaceId, sectionId));
    if (raw == null || raw === '') return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed as CalendarDay[];
  } catch {
    return [];
  }
}

export function writeAcademicSectionCalendarDaysToLocalStorage(
  workspaceId: string,
  sectionId: string,
  days: CalendarDay[]
): void {
  if (!workspaceId || !sectionId || sectionId === 'base' || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      academicSectionCalendarDaysStorageKey(workspaceId, sectionId),
      JSON.stringify(days)
    );
    emitAcademicSectionCalendarChange(workspaceId, sectionId);
  } catch {
    /* ignore */
  }
}

/** @deprecated 학년도만 필요할 때 — `readAcademicSectionSettingsFromLocalStorage` 사용 권장 */
export function readAcademicSectionYearFromLocalStorage(workspaceId: string, sectionId: string): number | null {
  const s = readAcademicSectionSettingsFromLocalStorage(workspaceId, sectionId);
  return s?.academicYear ?? null;
}

/** 학사일정 페이지 `computeForSection`과 동일한 1·2학기 구간 */
export function computeSectionTermRanges(
  settings: AcademicSectionCalendarSettings
): SectionTermRanges | null {
  const ys = (settings.yearStartsOn ?? '').trim();
  const ye = (settings.yearEndsOn ?? '').trim();
  const ss = (settings.summerBreakStartsOn ?? '').trim();
  const se = (settings.summerBreakEndsOn ?? '').trim();
  if (!ys || !ye || !ss || !se) return null;
  const term1End = addDays(parseISODate(ss), -1);
  const term1EndISO = toISODate(term1End);
  const term2Start = addDays(parseISODate(se), 1);
  const term2StartISO = toISODate(term2Start);
  const calendarStartsOn = ys < term2StartISO ? ys : term2StartISO;
  const calendarEndsOn = ye > term1EndISO ? ye : term1EndISO;
  return {
    term1StartsOn: ys,
    term1EndsOn: term1EndISO,
    term2StartsOn: term2StartISO,
    term2EndsOn: ye,
    calendarStartsOn,
    calendarEndsOn,
  };
}

export function buildAcademicTermsFromSectionRanges(
  workspaceId: string,
  academicYear: number,
  ranges: SectionTermRanges
): AcademicTerm[] {
  return [
    {
      id: `section-${workspaceId}-${academicYear}-1`,
      workspace_id: workspaceId,
      academic_year: academicYear,
      term_number: 1,
      starts_on: ranges.term1StartsOn,
      ends_on: ranges.term1EndsOn,
    },
    {
      id: `section-${workspaceId}-${academicYear}-2`,
      workspace_id: workspaceId,
      academic_year: academicYear,
      term_number: 2,
      starts_on: ranges.term2StartsOn,
      ends_on: ranges.term2EndsOn,
    },
  ];
}

function emptyContext(fallbackYear: number): CalendarLinkedContext {
  return {
    sectionId: 'base',
    academicYear: fallbackYear,
    terms: [],
    calendarStartsOn: '',
    calendarEndsOn: '',
    useDbCalendarDays: true,
    localCalendarDays: [],
    sectionSettings: null,
  };
}

function contextFromSectionSettings(
  workspaceId: string,
  sectionId: string,
  settings: AcademicSectionCalendarSettings,
  useDbCalendarDays: boolean,
  localCalendarDays: CalendarDay[]
): CalendarLinkedContext {
  const ranges = computeSectionTermRanges(settings);
  if (!ranges) {
    return {
      sectionId,
      academicYear: settings.academicYear,
      terms: [],
      calendarStartsOn: '',
      calendarEndsOn: '',
      useDbCalendarDays,
      localCalendarDays,
      sectionSettings: settings,
    };
  }
  return {
    sectionId,
    academicYear: settings.academicYear,
    terms: buildAcademicTermsFromSectionRanges(workspaceId, settings.academicYear, ranges),
    calendarStartsOn: ranges.calendarStartsOn,
    calendarEndsOn: ranges.calendarEndsOn,
    useDbCalendarDays,
    localCalendarDays,
    sectionSettings: settings,
  };
}

export type ResolveCalendarLinkedOptions = {
  /** 저장·invalidate 직후 등 서버 섹션 JSON을 꼭 다시 읽을 때만 true */
  skipLocalCache?: boolean;
  /** true면 REST 없이 LS만 (메인 프레젠테이션). 캐시 없으면 기본값 */
  localOnly?: boolean;
};

/**
 * 클래스가 속한 학사일정 섹션 설정으로 학년도·학기·공휴일 소스를 결정합니다.
 * 비(base) 섹션은 LS 설정·공휴일을 그대로 쓰며 DB 최신 학년도로 덮어쓰지 않습니다.
 */
export async function resolveCalendarLinkedContext(
  workspaceId: string,
  gradeId: string | null | undefined,
  fallbackYear: number,
  options?: ResolveCalendarLinkedOptions
): Promise<CalendarLinkedContext> {
  if (!workspaceId) return emptyContext(fallbackYear);

  const localOnly = options?.localOnly === true;

  let sections: PageSectionLayout[];
  if (localOnly) {
    sections = loadAcademicCalendarSectionsCache(workspaceId) ?? [{ id: 'base', gradeIds: [] }];
  } else {
    try {
      sections = await getAcademicCalendarSections(workspaceId, {
        skipLocalCache: options?.skipLocalCache === true,
      });
    } catch {
      sections = [{ id: 'base', gradeIds: [] }];
    }
  }

  const gid = (gradeId ?? '').trim();
  const sec = gid ? sections.find((s) => Array.isArray(s.gradeIds) && s.gradeIds.includes(gid)) : undefined;
  const sectionId = sec?.id ?? 'base';

  if (sectionId !== 'base') {
    /* 편집 중 LS가 서버 JSON보다 최신인 경우가 많아 LS를 우선합니다. */
    const settings =
      readAcademicSectionSettingsFromLocalStorage(workspaceId, sectionId) ??
      sec?.academicCalendarSettings ??
      defaultAcademicSectionSettings(fallbackYear);
    const lsDays = readAcademicSectionCalendarDaysFromLocalStorage(workspaceId, sectionId);
    const localDays = lsDays.length > 0 ? lsDays : (sec?.academicCalendarDays ?? []);
    return contextFromSectionSettings(workspaceId, sectionId, settings, false, localDays);
  }

  /* 학사일정 페이지 base는 `todayYear`로 DB를 읽음 — DB "최신 학년도"와 맞춥니다. */
  const dbYear = fallbackYear;
  if (localOnly) {
    const fromLs =
      loadAcademicYearSettingsCache(workspaceId, dbYear) ??
      loadAcademicYearSettingsCache(workspaceId, dbYear - 1);
    const settings: AcademicSectionCalendarSettings = fromLs
      ? {
          academicYear: fromLs.academic_year,
          yearStartsOn: fromLs.year_starts_on,
          yearEndsOn: fromLs.year_ends_on,
          summerBreakStartsOn: fromLs.summer_break_starts_on,
          summerBreakEndsOn: fromLs.summer_break_ends_on,
          winterBreakStartsOn: fromLs.winter_break_starts_on ?? '',
          winterBreakEndsOn: fromLs.winter_break_ends_on ?? '',
        }
      : defaultAcademicSectionSettings(dbYear);
    return contextFromSectionSettings(workspaceId, 'base', settings, true, []);
  }

  let row = await getAcademicYearSettings(workspaceId, dbYear).catch(() => null);
  if (!row) {
    const prevYear = dbYear - 1;
    row = await getAcademicYearSettings(workspaceId, prevYear).catch(() => null);
  }
  const settings: AcademicSectionCalendarSettings = row
    ? {
        academicYear: row.academic_year,
        yearStartsOn: row.year_starts_on,
        yearEndsOn: row.year_ends_on,
        summerBreakStartsOn: row.summer_break_starts_on,
        summerBreakEndsOn: row.summer_break_ends_on,
        winterBreakStartsOn: row.winter_break_starts_on ?? '',
        winterBreakEndsOn: row.winter_break_ends_on ?? '',
      }
    : defaultAcademicSectionSettings(dbYear);

  return contextFromSectionSettings(workspaceId, 'base', settings, true, []);
}

/** @deprecated — `resolveCalendarLinkedContext` 사용 */
export async function resolveCalendarLinkedAcademicYear(
  workspaceId: string,
  gradeId: string | null | undefined,
  fallbackYear: number
): Promise<number> {
  const ctx = await resolveCalendarLinkedContext(workspaceId, gradeId, fallbackYear);
  return ctx.academicYear;
}
